// ./src/components/features/CreateEventWizard/steps/GamesStep.tsx
//
// "Which games will you play?" — shown when the Party games feature is on (ROADMAP A2b #62; owner, 2026-09-29).
//
// ⭐ The event TYPE's games come first: a Christmas party's offer names Secret Santa, networking's Human Bingo
// (`gamePresets` on the seeded `party_games` offer). Each is a one-tap starter that opens the Games page's own
// `GameDrawer` already set up from that preset (`defaultPresetId` → the drawer's own `applyPreset`), so the wizard can
// never build a different game from the one the drawer would. Roles are DEALT at the party, from the event's Games page.

import React, { useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useOptionalEventSetupCatalogContext } from '@/contexts';
import { useCreateGate, useEventGames, useToast } from '@/hooks';
import { EventGame, EventGameService } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { GameDrawer } from '@/pages/private/EventGamesPage/GameDrawer';
import { GAME_PRESETS, findPreset } from '@/pages/private/EventGamesPage/presets';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

export const GamesStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { draft, createdEventId: eventId, saving } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();

  const { games, isLoaded, error, reload } = useEventGames(eventId);
  const gate = useCreateGate(FEATURE_CODES.ENGAGEMENT, { eventId });

  const [drawer, setDrawer] = useState<{ game: EventGame | null; presetId: string | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<EventGame | null>(null);
  const [removing, setRemoving] = useState(false);

  // The type's own games, then nothing else — the drawer's preset list offers every other one.
  const type = catalog?.findType(draft.eventCategoryId) ?? null;
  const typePresets = (type?.offers?.find((o) => o.featureCode === 'party_games')?.options?.gamePresets ?? [])
    .map((id) => GAME_PRESETS.find((p) => p.id === id))
    .filter((p): p is (typeof GAME_PRESETS)[number] => !!p)
    .filter((p) => !games.some((g) => g.preset === p.id));

  const openNew = (presetId: string | null) => {
    if (!gate.canCreate) {
      return;
    }

    setDrawer({ game: null, presetId });
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await EventGameService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    await reload();
  };

  if (!eventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.games.intro', {
          defaultValue:
            'Set up the games guests play on their phones. You deal the roles or draw names at the party, from the event’s Games page.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.games.title', { defaultValue: 'Games' })}
        addLabel={t('events:wizard.games.add', { defaultValue: 'Add a game' })}
        onAdd={() => openNew(null)}
        gate={gate}
        gateWhat={t('events:wizard.games.another', { defaultValue: 'another game' })}
        disabled={saving}
        starters={typePresets.map((p) => ({ key: p.id, label: p.label, icon: 'DeviceGamepad2', onClick: () => openNew(p.id) }))}
        isLoaded={isLoaded}
        readError={error}
        onRetry={() => void reload()}
        errorTitle={t('events:wizard.games.loadFailed', { defaultValue: 'Could not load the games' })}
        operation="eventGameList"
        emptyText={t('events:wizard.games.none', { defaultValue: 'No games yet.' })}
        isEmpty={games.length === 0}
      >
        {games.map((g) => (
          <WizardItemRow
            key={g.id}
            icon="DeviceGamepad2"
            title={g.name ?? ''}
            subtitle={g.preset ? (findPreset(g.preset)?.label ?? null) : t('events:wizard.games.custom', { defaultValue: 'Your own game' })}
            disabled={saving}
            onEdit={() => setDrawer({ game: g, presetId: null })}
            onRemove={() => setPendingRemove(g)}
          />
        ))}
      </WizardListSection>

      <GameDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        eventId={eventId}
        game={drawer?.game ?? null}
        defaultPresetId={drawer?.presetId ?? null}
        onSaved={() => {
          setDrawer(null);
          void reload();
        }}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.games.removeTitle', { defaultValue: 'Remove this game?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};
